import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatNumber } from '../../../components/design-system/admin/format';
import { LIMIT_RANGES } from './plan-catalog';
import type { LimitKey } from './plan-catalog';
import { usePlanLabels } from './use-plan-labels';

/** The ⓘ after a label: what the number counts. */
export function InfoTip({ text, label }: { text: string; label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex cursor-help items-center rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <Info className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{text}</TooltipContent>
    </Tooltip>
  );
}

/**
 * One plan limit: an editable number, an ∞ toggle for Unlimited (-1), and a
 * slider over the admin range. Lives outside the editor so typing in it does
 * not remount it (and lose focus) on every keystroke.
 */
export function PlanLimitField({ limitKey, value, onChange }: { limitKey: LimitKey; value: number; onChange: (value: number) => void }) {
  const { t } = useTranslation();
  const labels = usePlanLabels();
  const { max, step } = LIMIT_RANGES[limitKey];
  const unlimited = value === -1;
  const numeric = Number.isFinite(value) && value >= 0 ? value : 0;
  const label = labels.limitLabel(limitKey);

  return (
    <div data-testid={`limit-row-${limitKey}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          <span className="truncate text-sm text-muted-foreground">{label}</span>
          <InfoTip text={labels.limitInfo(limitKey)} label={t('ADMIN_PLANS.EDIT.ABOUT', { name: label })} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {unlimited ? (
            <span className="text-sm font-medium">{t('ADMIN_SHELL.COMMON.UNLIMITED')}</span>
          ) : (
            <Input
              type="number"
              className="h-8 w-28 text-end tabular-nums"
              value={numeric}
              min={0}
              onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
              data-testid={`limit-input-${limitKey}`}
              aria-label={label}
            />
          )}
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => onChange(unlimited ? 0 : -1)}
            data-testid={`limit-unlimited-${limitKey}`}
            aria-label={t('ADMIN_PLANS.EDIT.UNLIMITED_TOGGLE', { name: label })}
            aria-pressed={unlimited}
            className={cn('text-base', unlimited ? 'border-foreground text-foreground' : 'text-muted-foreground')}
          >
            ∞
          </Button>
        </div>
      </div>
      <Slider
        className="mt-3"
        value={[Math.min(numeric, max)]}
        min={0}
        max={max}
        step={step}
        disabled={unlimited}
        aria-label={label}
        onValueChange={([nv]) => onChange(Number(nv))}
      />
      <div className="mt-1.5 flex justify-between text-xs text-muted-foreground tabular-nums">
        <span>{formatNumber(0)}</span>
        <span>{formatNumber(max)}</span>
      </div>
    </div>
  );
}
